import { useEffect, useState, useCallback, useRef } from "react";
import { toast } from "sonner";
import { Camera, Upload, Trash2, Loader2 } from "lucide-react";
import { API, api, errMsg } from "../lib/api";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Dialog, DialogContent } from "./ui/dialog";
import { PlainSelect, Empty } from "./Common";

const src = (p) => `${API}/photos/${p.id}/file`;

function Thumb({ p, onOpen }) {
  return (
    <button onClick={() => onOpen(p)} data-testid={`photo-${p.id}`} className="group relative aspect-square overflow-hidden rounded-lg border bg-muted">
      <img src={src(p)} alt={p.caption || p.kind} loading="lazy" className="h-full w-full object-cover transition-transform group-hover:scale-105" />
      <span className={`absolute left-1.5 top-1.5 rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase ${p.kind === "after" ? "bg-primary text-primary-foreground" : "bg-black/70 text-white"}`}>{p.kind}</span>
      <span className="absolute bottom-0 inset-x-0 bg-black/50 text-white text-[10px] px-1.5 py-0.5 text-left">{p.taken_at.slice(0, 10)}</span>
    </button>
  );
}

export function Photos({ client }) {
  const [items, setItems] = useState([]);
  const [kind, setKind] = useState("before");
  const [area, setArea] = useState("");
  const [busy, setBusy] = useState(false);
  const [view, setView] = useState(null);
  const cam = useRef(null);
  const file = useRef(null);
  const load = useCallback(() => api.get(`/clients/${client.id}/photos`).then((r) => setItems(r.data)), [client.id]);
  useEffect(() => { load(); }, [load]);

  const upload = async (files) => {
    setBusy(true);
    try {
      for (const f of files) {
        const fd = new FormData();
        fd.append("file", f); fd.append("kind", kind); fd.append("area", area);
        await api.post(`/clients/${client.id}/photos`, fd);
      }
      toast.success("Photo saved"); load();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  const remove = async (p) => { if (window.confirm("Delete this photo?")) { await api.delete(`/photos/${p.id}`); setView(null); load(); } };
  const areas = [...new Set(items.map((p) => p.area || "General"))];

  return (
    <div className="space-y-5" data-testid="client-photos">
      <div className="flex flex-wrap items-center gap-2 rounded-[var(--radius)] border bg-card p-4">
        <PlainSelect data-testid="photo-kind-select" className="!w-36" value={kind} onChange={(e) => setKind(e.target.value)}>
          <option value="before">Before</option><option value="after">After</option><option value="progress">Progress</option>
        </PlainSelect>
        <Input data-testid="photo-area-input" className="w-48" placeholder="Area (e.g. Forehead, Lips)" value={area} onChange={(e) => setArea(e.target.value)} />
        <Button className="rounded-full" data-testid="photo-camera-btn" disabled={busy} onClick={() => cam.current.click()}><Camera className="h-4 w-4 mr-1.5" />Take photo</Button>
        <Button variant="outline" className="rounded-full" data-testid="photo-upload-btn" disabled={busy} onClick={() => file.current.click()}><Upload className="h-4 w-4 mr-1.5" />Upload</Button>
        {busy && <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />}
        <input ref={cam} type="file" accept="image/*" capture="environment" className="hidden" data-testid="photo-camera-input" onChange={(e) => upload([...e.target.files])} />
        <input ref={file} type="file" accept="image/*" multiple className="hidden" data-testid="photo-file-input" onChange={(e) => upload([...e.target.files])} />
      </div>
      {!items.length && <Empty text="No photos yet" />}
      {areas.map((a) => {
        const list = items.filter((p) => (p.area || "General") === a);
        const before = [...list].reverse().find((p) => p.kind === "before");
        const after = list.find((p) => p.kind === "after");
        return (
          <section key={a} className="space-y-3" data-testid={`photo-area-${a}`}>
            <h3 className="text-sm">{a}</h3>
            {before && after && (
              <div className="grid grid-cols-2 gap-2 max-w-xl" data-testid="photo-compare">
                {[before, after].map((p) => <figure key={p.id}><img src={src(p)} alt={p.kind} className="aspect-[3/4] w-full object-cover rounded-lg border" />
                  <figcaption className="text-xs text-center mt-1 uppercase tracking-wide">{p.kind} · {p.taken_at.slice(0, 10)}</figcaption></figure>)}
              </div>
            )}
            <div className="grid grid-cols-3 sm:grid-cols-5 lg:grid-cols-7 gap-2">{list.map((p) => <Thumb key={p.id} p={p} onOpen={setView} />)}</div>
          </section>
        );
      })}
      <Dialog open={!!view} onOpenChange={(o) => !o && setView(null)}>
        <DialogContent className="max-w-3xl" data-testid="photo-viewer">
          {view && <><img src={src(view)} alt="" className="max-h-[75vh] w-full object-contain rounded-md" />
            <div className="flex items-center justify-between text-sm"><span className="capitalize">{view.kind} · {view.area || "General"} · {new Date(view.taken_at).toLocaleString()} · {view.uploaded_by}</span>
              <Button size="sm" variant="ghost" data-testid="photo-delete-btn" onClick={() => remove(view)}><Trash2 className="h-4 w-4 mr-1" />Delete</Button></div></>}
        </DialogContent>
      </Dialog>
    </div>
  );
}
